import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Trash2, Copy, Download, Send, Clapperboard, Plug, CheckCircle2, CalendarClock, X, Camera } from "lucide-react";
import client, { assetUrl, API } from "@/lib/glossyApi";
import { Header } from "./shared";
import { CampaignComposer } from "./CampaignComposer";
import { Outreach } from "./Outreach";
import { Planner } from "./Planner";

const absUrl = (path) => (path?.startsWith("http") ? path : `${API}/files/${path}`);

function ShowcasePanel({ onMade }) {
  const [arts, setArts] = useState([]);
  const [adMembers, setAdMembers] = useState([]);
  const [sel, setSel] = useState("");
  const [bigLine, setBigLine] = useState("We made this.");
  const [smallLine, setSmallLine] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.get("/admin/articles").then((r) => {
      setArts((r.data || []).filter((a) => a.status === "published" && (a.cover_image || a.fallback_image)));
    }).catch(() => {});
    client.get("/admin/members").then((r) => {
      const list = (r.data || []).filter((m) => m.package?.advert?.image);
      setAdMembers(list);
    }).catch(() => {});
  }, []);
  const make = async () => {
    if (!sel) { toast.error("Pick a piece from the magazine first."); return; }
    setBusy(true);
    try {
      const payload = sel.startsWith("advert:")
        ? { advert_member_id: sel.slice(7), big_line: bigLine, small_line: smallLine }
        : { article_id: sel, big_line: bigLine, small_line: smallLine };
      await client.post("/admin/social/showcase", payload);
      toast.success("Showcase tile ready — it's in your drafts below, post when happy.");
      onMade?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't make the tile."); }
    setBusy(false);
  };
  return (
    <div className="border border-ink/15 p-5 sm:p-6 mb-8" data-testid="showcase-panel">
      <p className="overline text-ink">Showcase the magazine — "we made this"</p>
      <p className="font-sans text-xs text-ink/55 mt-1 mb-4">Turn any real advert, feature or interview into an Instagram tile + Story — its real photography, untouched — so people see exactly what members get.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select value={sel} onChange={(e) => setSel(e.target.value)}
          className="bg-cream border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold sm:col-span-2" data-testid="showcase-article-select">
          <option value="">Pick a piece from the magazine…</option>
          {adMembers.length > 0 && (
            <optgroup label="Adverts">
              {adMembers.map((m) => (
                <option key={`ad-${m.id}`} value={`advert:${m.id}`}>advert — {m.package.advert.headline || m.business_name}</option>
              ))}
            </optgroup>
          )}
          {arts.map((a) => (
            <option key={a.id} value={a.id}>{(a.type || "piece").replace("_", " ")} — {a.title}</option>
          ))}
        </select>
        <input value={bigLine} onChange={(e) => setBigLine(e.target.value)} placeholder="Big line"
          className="bg-cream border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold" data-testid="showcase-big-line" />
        <input value={smallLine} onChange={(e) => setSmallLine(e.target.value)} placeholder="Small line (blank = written for you)"
          className="bg-cream border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold" data-testid="showcase-small-line" />
      </div>
      <button onClick={make} disabled={busy} className="mt-3 overline text-[11px] px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="showcase-make-btn">
        {busy ? "Making…" : "Make showcase tile (free)"}
      </button>
    </div>
  );
}

function ClipsPanel() {
  const [clips, setClips] = useState([]);
  const load = () => client.get("/admin/videos").then((r) => setClips((r.data || []).filter((v) => !["article_reel", "demo_reel"].includes(v.purpose || "")))).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!clips.some((v) => v.status === "processing")) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [clips]);
  const del = async (v) => {
    if (!window.confirm("Delete this motion clip?")) return;
    try { await client.delete(`/admin/videos/${v.id}`); toast.success("Clip deleted."); load(); }
    catch { toast.error("Could not delete that clip."); }
  };
  if (clips.length === 0) return null;
  return (
    <div className="border border-ink/20 bg-white p-5 sm:p-6 mb-8" data-testid="clips-panel">
      <p className="overline text-ink mb-1">Motion clips</p>
      <p className="font-sans text-xs text-ink/60 mb-4">Every animated tile lands here — download and post it, or use it in the Instagram app as a reel.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {clips.map((v) => (
          <div key={v.id} className="border border-ink/15" data-testid={`clip-${v.id}`}>
            {v.status === "done" && v.video_path ? (
              <video controls preload="metadata" src={absUrl(v.video_path)} className="w-full aspect-[9/16] object-cover bg-ink" data-testid={`clip-video-${v.id}`} />
            ) : v.status === "processing" ? (
              <div className="w-full aspect-[9/16] bg-ink flex items-center justify-center"><p className="overline text-[9px] text-cream/70 animate-pulse">Animating…</p></div>
            ) : (
              <div className="w-full aspect-[9/16] bg-ink flex items-center justify-center p-3"><p className="font-mono text-[9px] text-red-400 text-center">{v.error || "Failed — try again."}</p></div>
            )}
            <div className="p-2 flex items-center gap-1.5">
              {v.status === "done" && v.video_path && (
                <a href={`${API}/files/${v.video_path}?download=1`} className="overline text-[9px] px-2 py-1.5 border border-ink/30 hover:bg-ink hover:text-cream transition-colors" data-testid={`clip-download-${v.id}`}>Download</a>
              )}
              <button onClick={() => del(v)} className="overline text-[9px] px-2 py-1.5 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors ml-auto" data-testid={`clip-delete-${v.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoReelPanel() {
  const [status, setStatus] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const poll = () => client.get("/admin/social/demo-reel-status").then((r) => {
    setStatus(r.data);
    if (r.data?.caption?.caption && !caption) setCaption(r.data.caption.caption);
  }).catch(() => {});
  useEffect(() => { poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (status?.ig_status !== "publishing") return;
    const t = setInterval(poll, 6000);
    return () => clearInterval(t);
  }, [status?.ig_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async () => {
    if (!window.confirm("Post the S/ME demo reel to Instagram now? It goes live immediately on the S/ME account.")) return;
    setBusy(true);
    try {
      await client.post("/admin/social/demo-reel-publish", { caption });
      toast.success("Demo reel on its way to Instagram — live in a minute or two. Pin it from the app once it lands.");
      poll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed."); }
    setBusy(false);
  };

  const publishing = status?.ig_status === "publishing";
  return (
    <div className="border border-gloss-gold/60 bg-gloss-gold/10 p-5 sm:p-6 mb-8" data-testid="demo-reel-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clapperboard size={18} className="text-ink" />
          <div>
            <p className="overline text-ink">The S/ME demo reel — your member-getting ad</p>
            <p className="font-sans text-xs text-ink/55 mt-0.5">83 seconds, narrated. Posts to the S/ME Instagram; pin it to the top of the grid from the app afterwards.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API}/files/the-glossy/productions/demo/sme-demo-reel.mp4?download=1`} className="overline text-[10px] px-3 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid="demo-reel-download">
            <Download size={12} /> Download
          </a>
          {status?.ig_status === "posted" && status?.ig_permalink && (
            <a href={status.ig_permalink} target="_blank" rel="noreferrer" className="overline text-[10px] px-3 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="demo-reel-live">On Instagram ✓</a>
          )}
          <button onClick={() => setOpen(!open)} className="overline text-[10px] px-3 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors flex items-center gap-1.5" data-testid="demo-reel-toggle">
            <Send size={12} /> {publishing ? "Posting…" : status?.ig_status === "posted" ? "Post again" : "Post to Instagram"}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4">
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5} placeholder="Loading suggested caption…"
            className="w-full bg-cream border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold" data-testid="demo-reel-caption" />
          <button onClick={post} disabled={busy || publishing}
            className="mt-2 overline text-[11px] px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="demo-reel-post-btn">
            {publishing ? "Posting — a minute or two…" : "Post demo reel now"}
          </button>
        </div>
      )}
      {status?.ig_status === "failed" && status?.ig_error && (
        <p className="font-mono text-[10px] text-red-600 mt-2" data-testid="demo-reel-error">{status.ig_error}</p>
      )}
    </div>
  );
}

function ConnectPanel({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [igId, setIgId] = useState(settings?.instagram_user_id || "");
  const [videoKey, setVideoKey] = useState("");
  const [emailKey, setEmailKey] = useState("");
  const [appId, setAppId] = useState("948352074948347");
  const [appSecret, setAppSecret] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => { if (settings?.instagram_app_id) setAppId(settings.instagram_app_id); }, [settings?.instagram_app_id]);

  const oauthConnect = async (copyOnly = false) => {
    if (!appId.trim() || (!appSecret.trim() && !settings?.instagram_app_ready)) { toast.error("Pop in both the App ID and App Secret first."); return; }
    setBusy("oauth");
    try {
      const r = await client.post("/admin/social/instagram/authorize-url", {
        app_id: appId.trim(), app_secret: appSecret.trim(),
        redirect_uri: `${window.location.origin}/api/admin/social/instagram/callback`,
      });
      if (copyOnly) {
        await navigator.clipboard.writeText(r.data.url);
        toast.success("Connect link copied — send it to your phone (WhatsApp/iMessage yourself), open it there and log in. The connection saves automatically.");
      } else {
        window.open(r.data.url, "_blank");
        toast.success("Instagram login opened in a new tab — log in there, then come back and click Check connection.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the connection."); }
    setBusy("");
  };

  const fbConnect = async () => {
    setBusy("fb");
    try {
      const r = await client.post("/admin/social/instagram/authorize-url-fb", {
        redirect_uri: `${window.location.origin}/api/admin/social/instagram/fb-callback`,
      });
      window.open(r.data.url, "_blank");
      toast.success("Facebook login opened — approve it, then come back and click Check connection.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the Facebook connection."); }
    setBusy("");
  };

  const save = async () => {
    setBusy("save");
    try {
      const body = {};
      if (token.trim()) body.instagram_access_token = token.trim();
      if (igId.trim()) body.instagram_user_id = igId.trim();
      if (videoKey.trim()) body.fal_api_key = videoKey.trim();
      if (emailKey.trim()) body.resend_api_key = emailKey.trim();
      const r = await client.patch("/admin/settings", body);
      onSaved(r.data); setToken(""); setVideoKey(""); setEmailKey("");
      toast.success("Saved. Your keys are in and ready.");
    } catch { toast.error("Could not save keys."); }
    setBusy("");
  };
  const check = async () => {
    setBusy("check");
    try { const r = await client.get("/admin/social/instagram-health"); toast.success(`Connected as @${r.data.username}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not connected yet."); }
    setBusy("");
  };
  const testWelcome = async () => {
    setBusy("welcome");
    try { const r = await client.post("/admin/social/test-welcome-email", { origin: window.location.origin }); toast.success(`Test welcome email sent to ${r.data.sent_to} — check your inbox.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send test email."); }
    setBusy("");
  };

  return (
    <div className="border border-ink/15 mb-10" data-testid="connect-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-secondary transition-colors" data-testid="connect-toggle">
        <span className="flex items-center gap-3">
          <Plug size={16} />
          <span className="overline">Connect Instagram & Motion</span>
        </span>
        <span className="flex items-center gap-4">
          <span className={`overline flex items-center gap-1.5 ${settings?.instagram_connected ? "text-green-700" : "text-ink/40"}`} data-testid="ig-status">
            {settings?.instagram_connected && <CheckCircle2 size={13} />} Instagram {settings?.instagram_connected ? "connected" : "not connected"}
          </span>
          <span className={`overline flex items-center gap-1.5 ${settings?.video_key_added ? "text-green-700" : "text-ink/40"}`} data-testid="motion-status">
            {settings?.video_key_added && <CheckCircle2 size={13} />} Motion {settings?.video_key_added ? "on" : "off"}
          </span>
          <span className={`overline flex items-center gap-1.5 ${settings?.email_connected ? "text-green-700" : "text-ink/40"}`} data-testid="email-status">
            {settings?.email_connected && <CheckCircle2 size={13} />} Email {settings?.email_connected ? "on" : "off"}
          </span>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6 pt-2 space-y-4 border-t border-ink/10">
          <div className="border border-gloss-gold/50 bg-gloss-gold/5 p-4 space-y-3">
            <p className="overline">Easiest way — connect in one click</p>
            <p className="font-sans text-sm text-ink/60">Paste your Meta App ID and App Secret once, click Connect, and log in with your Instagram account on Instagram's own page. We capture the key automatically — no console wrestling.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <input value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="Instagram App ID (e.g. 9483520…)" className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="ig-app-id-input" />
              <input value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={settings?.instagram_app_ready ? "App Secret saved ✓ — paste only to replace" : "Instagram App Secret"} className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="ig-app-secret-input" />
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => oauthConnect(false)} disabled={!!busy} className="bg-ink text-cream px-5 py-2.5 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="ig-oauth-connect-btn">
                {busy === "oauth" ? "Opening Instagram…" : "Connect Instagram"}
              </button>
              <button onClick={() => oauthConnect(true)} disabled={!!busy} className="border border-ink/30 px-5 py-2.5 overline hover:border-ink transition-colors disabled:opacity-60" data-testid="ig-copy-link-btn" title="Instagram login playing up on this computer? Do it from your phone instead.">
                Copy link for your phone
              </button>
              <button onClick={fbConnect} disabled={!!busy} className="border border-ink/30 px-5 py-2.5 overline hover:border-ink transition-colors disabled:opacity-60" data-testid="ig-fb-connect-btn" title="Instagram login blocked? Log in with Facebook instead — same result.">
                {busy === "fb" ? "Opening Facebook…" : "Connect with Facebook instead"}
              </button>
            </div>
            <p className="font-sans text-xs text-ink/45">Login not working on this computer? Click "Copy link for your phone", message it to yourself, open it on your phone (where Instagram already knows you) and log in there — the connection saves automatically either way.</p>
            <p className="font-sans text-xs text-ink/45">One-time setup in Meta: on your app's <b>Set up Instagram business login</b> section → Business login settings → add this redirect URL: <span className="font-mono select-all break-all">{`${window.location.origin}/api/admin/social/instagram/callback`}</span></p>
          </div>
          <p className="font-sans text-sm text-ink/60">Or paste your keys manually — every button below is already wired and switches to fully automatic the moment they're in. Posting goes to S/ME's own Instagram; members receive the finished assets in their Share Kit.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="overline text-ink/50 mb-1">Instagram access token</p>
              <textarea value={token} onChange={(e) => setToken(e.target.value)} rows={2} placeholder="Paste your Meta access token…" className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="ig-token-input" />
            </div>
            <div className="space-y-4">
              <div>
                <p className="overline text-ink/50 mb-1">Instagram account ID</p>
                <input value={igId} onChange={(e) => setIgId(e.target.value)} placeholder="e.g. 1784140102…" className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="ig-id-input" />
              </div>
              <div>
                <p className="overline text-ink/50 mb-1">Video key (for Animate)</p>
                <input value={videoKey} onChange={(e) => setVideoKey(e.target.value)} placeholder="Paste your fal.ai key…" className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="video-key-input" />
              </div>
              <div>
                <p className="overline text-ink/50 mb-1">Email key (welcome emails & newsletters)</p>
                <input value={emailKey} onChange={(e) => setEmailKey(e.target.value)} placeholder="Paste your Resend key (re_…)" className="w-full border border-ink/20 px-3 py-2 font-mono text-xs focus:outline-none focus:border-ink" data-testid="email-key-input" />
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={!!busy} className="bg-ink text-cream px-5 py-2.5 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="save-keys-btn">
              {busy === "save" ? "Saving…" : "Save keys"}
            </button>
            <button onClick={check} disabled={!!busy} className="border border-ink/30 px-5 py-2.5 overline hover:border-ink transition-colors disabled:opacity-60" data-testid="check-connection-btn">
              {busy === "check" ? "Checking…" : "Check connection"}
            </button>
            <button onClick={testWelcome} disabled={!!busy} className="border border-ink/30 px-5 py-2.5 overline hover:border-ink transition-colors disabled:opacity-60" data-testid="test-welcome-btn">
              {busy === "welcome" ? "Sending…" : "Send me a test welcome email"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PostCard({ p, settings, onDelete, onChanged, mode = "instagram" }) {
  const [busy, setBusy] = useState("");
  const [tiles, setTiles] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [when, setWhen] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftCaption, setDraftCaption] = useState("");
  const [collab, setCollab] = useState("");
  const ig = mode === "instagram";

  const fullCaption = () => [p.caption, (p.hashtags || []).join(" ")].filter(Boolean).join("\n\n");

  const makeGraphic = async () => {
    setBusy("graphic");
    try {
      await client.post(`/admin/social/${p.id}/make-graphic`, {});
      toast.success("Branded tile + story card ready — the locked S/ME style.");
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't make the graphic."); }
    setBusy("");
  };

  const copyReelScript = async () => {
    try { await navigator.clipboard.writeText(p.reel_script || ""); toast.success("TikTok script copied."); }
    catch { toast.error("Copy failed."); }
  };

  const postStory = async () => {
    if (!settings?.instagram_connected) { toast.error("Connect Instagram first."); return; }
    setBusy("story");
    try {
      await client.post("/admin/social/instagram-publish-story", { image_url: absUrl(p.story_image), post_id: p.id });
      toast.success("Story posted to Instagram.");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Story post failed."); }
    setBusy("");
  };

  const reshoot = async () => {
    setBusy("reshoot");
    try { await client.post(`/admin/social/${p.id}/reshoot`); toast.success("Fresh shot ready — same words, new photograph."); setTiles(null); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reshoot failed — try again."); }
    setBusy("");
  };

  const saveCaption = async () => {
    setBusy("caption");
    try { await client.patch(`/admin/social/${p.id}`, { caption: draftCaption }); toast.success("Caption updated."); setEditing(false); onChanged?.(); }
    catch { toast.error("Could not save."); }
    setBusy("");
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(fullCaption()); toast.success(ig ? "Caption copied — ready for Instagram." : "Caption copied — ready for TikTok."); }
    catch { toast.error("Copy failed."); }
  };

  const schedule = async () => {
    if (!when) { toast.error("Pick a date and time first."); return; }
    if (!settings?.instagram_connected) { toast.error("Connect Instagram first — scheduled posts publish automatically."); return; }
    setBusy("schedule");
    try {
      await client.patch(`/admin/social/${p.id}/schedule`, {
        schedule_at: new Date(when).toISOString(),
        image_url: absUrl(tiles?.feed || p.image),
        caption: fullCaption(),
      });
      toast.success("Scheduled — it will go out automatically.");
      setShowSchedule(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule."); }
    setBusy("");
  };

  const unschedule = async () => {
    setBusy("schedule");
    try { await client.patch(`/admin/social/${p.id}/schedule`, { schedule_at: null }); toast.success("Schedule removed."); onChanged?.(); }
    catch { toast.error("Could not update."); }
    setBusy("");
  };

  const makeTiles = async () => {
    if (!p.image) { toast.error("This post has no image."); return; }
    setBusy("tiles");
    try { const r = await client.post("/admin/social/tiles", { image: p.image }); setTiles(r.data); toast.success("Tiles ready — Feed 4:5 (1080×1350), Square 1:1, Reels/TikTok 9:16."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not make tiles."); }
    setBusy("");
  };

  const postInstagram = async () => {
    if (!settings?.instagram_connected) {
      toast.error("Instagram isn't connected yet — open Connect Instagram above and paste your key, then this becomes one-click.");
      window.open("https://www.instagram.com", "_blank");
      return;
    }
    setBusy("post");
    try {
      const imagePath = tiles?.feed || p.image;
      await client.post("/admin/social/instagram-publish", { image_url: absUrl(imagePath), caption: fullCaption(), post_id: p.id, collaborator: collab.trim() });
      toast.success(collab.trim() ? "Posted — collab invite sent to " + collab.trim() : "Posted to Instagram.");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed."); }
    setBusy("");
  };

  const animate = async () => {
    setBusy("animate");
    try { await client.post("/admin/social/animate", { image: p.image }); toast.success("Motion clip on its way."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Motion not available yet."); }
    setBusy("");
  };

  return (
    <div className="border border-ink/15 p-6" data-testid={`social-post-${p.id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline text-gloss-silver">{p.post_type}{p.business_name ? ` · ${p.business_name}` : ""}{p.posted_instagram ? " · POSTED" : ""}{p.posted_story ? " · STORY LIVE" : ""}{p.status === "scheduled" ? " · SCHEDULED" : ""}{p.status === "schedule_failed" ? " · FAILED" : ""}</span>
        <button onClick={() => onDelete(p)} className="p-1.5 hover:text-red-500 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
      </div>
      {p.status === "scheduled" && p.schedule_at && (
        <div className="flex items-center justify-between bg-gloss-gold/10 border border-gloss-gold/40 px-3 py-2 mb-3" data-testid={`scheduled-banner-${p.id}`}>
          <span className="font-sans text-xs flex items-center gap-1.5"><CalendarClock size={13} /> Going out {new Date(p.schedule_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <button onClick={unschedule} disabled={!!busy} className="p-1 hover:text-red-500 transition-colors" title="Cancel schedule" data-testid={`unschedule-${p.id}`}><X size={13} /></button>
        </div>
      )}
      {p.status === "schedule_failed" && <p className="font-sans text-xs text-red-600 mb-3" data-testid={`schedule-error-${p.id}`}>Auto-post failed: {p.schedule_error}</p>}
      {p.status === "designing" && (
        <div className="w-full aspect-[3/4] mb-3 border border-dashed border-ink/25 flex flex-col items-center justify-center gap-2" data-testid={`social-designing-${p.id}`}>
          <span className="overline text-gloss-gold animate-pulse">Designing…</span>
          <span className="font-sans text-xs text-ink/45 px-6 text-center">The desk is laying this one out — refresh in a minute.</span>
        </div>
      )}
      {p.status === "design_failed" && <p className="font-sans text-xs text-red-600 mb-3" data-testid={`design-error-${p.id}`}>Design failed: {p.error || "try again"} — delete this card and press Design it again.</p>}
      {assetUrl(p.image) && (
        <div className="relative mb-3">
          <img src={assetUrl(p.image)} alt="" className="w-full aspect-[3/4] object-cover" data-testid={`social-image-${p.id}`} />
          <span className="absolute bottom-2 right-2 bg-ink/80 text-cream font-mono text-[9px] uppercase tracking-widest px-2 py-1" data-testid={`tile-size-${p.id}`}>Feed 4:5 · 1080×1350</span>
        </div>
      )}
      {editing ? (
        <div className="space-y-2 mb-2" data-testid={`caption-editor-${p.id}`}>
          <textarea value={draftCaption} onChange={(e) => setDraftCaption(e.target.value)} rows={5} className="w-full border border-ink/20 px-3 py-2 font-sans text-sm focus:outline-none focus:border-ink" data-testid={`caption-input-${p.id}`} />
          <div className="flex gap-2">
            <button onClick={saveCaption} disabled={!!busy} className="bg-ink text-cream px-4 py-1.5 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`caption-save-${p.id}`}>{busy === "caption" ? "Saving…" : "Save"}</button>
            <button onClick={() => setEditing(false)} className="border border-ink/25 px-4 py-1.5 overline hover:border-ink transition-colors">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="font-sans text-base whitespace-pre-line leading-relaxed cursor-text" onClick={() => { setDraftCaption(p.caption || ""); setEditing(true); }} title="Click to edit the caption" data-testid={`caption-text-${p.id}`}>{p.caption}</p>
      )}
      {p.reel_script && (ig ? (
        <details className="mt-3 bg-secondary p-3" data-testid={`reel-details-${p.id}`}>
          <summary className="overline text-ink/50 cursor-pointer">Reel script — optional</summary>
          <p className="font-mono text-xs leading-relaxed mt-2">{p.reel_script}</p>
        </details>
      ) : (
        <div className="mt-3 bg-secondary p-3"><p className="overline text-ink/50 mb-1">TikTok script</p><p className="font-mono text-xs leading-relaxed">{p.reel_script}</p></div>
      ))}
      {p.hashtags?.length > 0 && <p className="font-mono text-xs text-ink/60 mt-3">{p.hashtags.join(" ")}</p>}

      {ig && p.image && !p.posted_instagram && (
        <input value={collab} onChange={(e) => setCollab(e.target.value)} placeholder="Collab @handle (optional) — post appears on their profile too"
          className="w-full border border-ink/15 px-3 py-2 mt-3 font-sans text-xs focus:outline-none focus:border-ink" data-testid={`collab-input-${p.id}`} />
      )}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-ink/10">
        <button onClick={copyCaption} className="overline px-3 py-2 border border-ink/25 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid={`copy-caption-${p.id}`}><Copy size={13} /> Copy caption</button>
        {!p.image && (
          <button onClick={makeGraphic} disabled={!!busy} className="overline px-3 py-2 bg-gloss-gold text-ink hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5 disabled:opacity-60" data-testid={`make-graphic-${p.id}`} title="Branded S/ME tile + story card — the one locked style, free, no AI">
            {busy === "graphic" ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />} {busy === "graphic" ? "Building…" : "Make graphic (free)"}
          </button>
        )}
        {p.post_type === "campaign" && p.tile_style !== "caption" && !p.posted_instagram && (
          <button onClick={reshoot} disabled={!!busy} className="overline px-3 py-2 border border-ink/25 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5 disabled:opacity-60" data-testid={`reshoot-${p.id}`} title="Same words, new photograph — shot in your House Look">
            {busy === "reshoot" ? <RefreshCw size={13} className="animate-spin" /> : <Camera size={13} />} {busy === "reshoot" ? "Reshooting…" : "Reshoot image"}
          </button>
        )}
        {p.image && <button onClick={makeTiles} disabled={!!busy} className="overline px-3 py-2 border border-ink/25 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5 disabled:opacity-60" data-testid={`make-tiles-${p.id}`}>
          {busy === "tiles" ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />} Make tiles
        </button>}
        {!ig && p.reel_script && <button onClick={copyReelScript} className="overline px-3 py-2 border border-ink/25 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid={`copy-reel-${p.id}`}><Copy size={13} /> Copy TikTok script</button>}
        {ig && <button onClick={postInstagram} disabled={!!busy} className={`overline px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-60 ${settings?.instagram_connected ? "bg-ink text-cream hover:bg-gloss-gold hover:text-ink" : "border border-ink/25 text-ink/60 hover:border-ink"}`} data-testid={`post-instagram-${p.id}`}>
          {busy === "post" ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />} {settings?.instagram_connected ? "Post now" : "Copy & open Instagram"}
        </button>}
        {ig && p.story_image && !p.posted_story && <button onClick={postStory} disabled={!!busy} className={`overline px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-60 ${settings?.instagram_connected ? "bg-ink text-cream hover:bg-gloss-gold hover:text-ink" : "border border-ink/25 text-ink/60 hover:border-ink"}`} data-testid={`post-story-${p.id}`} title="Publish the 9:16 as an Instagram Story">
          {busy === "story" ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />} Post story
        </button>}
        {p.image && <button onClick={animate} disabled={!!busy} className={`overline px-3 py-2 border flex items-center gap-1.5 transition-colors disabled:opacity-60 ${settings?.video_key_added ? "border-ink/25 hover:bg-ink hover:text-cream" : "border-ink/15 text-ink/40 hover:border-ink/40"}`} data-testid={`animate-${p.id}`} title={settings?.video_key_added ? "Turn this image into a motion clip" : "Add your video key to switch on"}>
          {busy === "animate" ? <RefreshCw size={13} className="animate-spin" /> : <Clapperboard size={13} />} Animate
        </button>}
        {ig && p.image && !p.posted_instagram && p.status !== "scheduled" && (
          <button onClick={() => setShowSchedule(!showSchedule)} className="overline px-3 py-2 border border-ink/25 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid={`schedule-toggle-${p.id}`}>
            <CalendarClock size={13} /> Schedule
          </button>
        )}
      </div>
      {showSchedule && (
        <div className="flex flex-wrap items-center gap-2 mt-3 bg-secondary p-3" data-testid={`schedule-form-${p.id}`}>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="border border-ink/20 px-3 py-2 font-sans text-sm bg-cream focus:outline-none focus:border-ink" data-testid={`schedule-input-${p.id}`} />
          {[["Tomorrow 9am", 1, 9], ["Tonight 7pm", 0, 19], ["+2 days 9am", 2, 9]].map(([label, d, hr]) => (
            <button key={label} onClick={() => {
              const t = new Date(); t.setDate(t.getDate() + d); t.setHours(hr, 0, 0, 0);
              if (t < new Date()) t.setDate(t.getDate() + 1);
              const pad2 = (n) => String(n).padStart(2, "0");
              setWhen(`${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}T${pad2(t.getHours())}:${pad2(t.getMinutes())}`);
            }} className="overline text-[10px] px-2.5 py-2 border border-ink/20 hover:border-ink transition-colors" data-testid={`quick-${label.replace(/\W+/g, "-")}-${p.id}`}>{label}</button>
          ))}
          <button onClick={schedule} disabled={!!busy} className="bg-ink text-cream px-4 py-2 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`schedule-confirm-${p.id}`}>
            {busy === "schedule" ? "Saving…" : "Set it"}
          </button>
          <p className="font-sans text-xs text-ink/50 w-full sm:w-auto">Publishes to Instagram automatically at that time.</p>
        </div>
      )}
      {p.story_image && (
        <div className="flex flex-wrap gap-2 mt-3">
          <a href={absUrl(p.story_image)} target="_blank" rel="noreferrer" className="overline px-3 py-1.5 bg-secondary hover:bg-gloss-gold/30 transition-colors" data-testid={`story-download-${p.id}`}>Story 9:16 · 1080×1920 ↓</a>
        </div>
      )}
      {tiles && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`tiles-${p.id}`}>
          {[["feed", "Feed 4:5 · 1080×1350"], ["square", "Square 1:1 · 1080×1080"], ["vertical", "Reels & TikTok 9:16 · 1080×1920"]].map(([k, label]) => (
            <a key={k} href={absUrl(tiles[k])} target="_blank" rel="noreferrer" className="overline px-3 py-1.5 bg-secondary hover:bg-gloss-gold/30 transition-colors" data-testid={`tile-${k}-${p.id}`}>{label} ↓</a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Social() {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [type, setType] = useState("article teaser");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("instagram");

  const load = () => client.get("/admin/social").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!context.trim()) { toast.error("Add some context."); return; }
    setBusy(true);
    try { await client.post("/admin/social/generate", { post_type: type, context }); toast.success("Post created."); setContext(""); await load(); }
    catch { toast.error("Generation failed."); }
    setBusy(false);
  };
  const remove = async (p) => { await client.delete(`/admin/social/${p.id}`); toast.success("Deleted."); load(); };

  const igPosts = posts.filter((p) => !p.platforms || p.platforms.includes("instagram"));
  const ttPosts = posts.filter((p) => !p.platforms || p.platforms.includes("tiktok"));

  const autoSchedule = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/social/auto-schedule", {});
      toast.success(r.data.scheduled > 0 ? `${r.data.scheduled} draft${r.data.scheduled > 1 ? "s" : ""} queued — one each morning at 9am.` : "No unscheduled drafts to queue.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not queue drafts."); }
    setBusy(false);
  };

  const TABS = [["instagram", "Instagram"], ["calendar", "Calendar"], ["tiktok", "TikTok"], ["campaign", "Campaign Composer"], ["outreach", "Outreach"]];

  return (
    <div>
      <Header kicker="Social Desk" title={tab === "tiktok" ? "TikTok" : tab === "instagram" ? "Instagram" : "Social"}>
        <p className="overline text-ink/50">{tab === "tiktok" ? "9:16 tiles & clips · manual upload" : "Tiles: 4:5 feed (1080×1350, S/ME branded) · 1:1 square · 9:16 stories"}</p>
      </Header>
      <div className="p-6 sm:p-10">
        <ConnectPanel settings={settings} onSaved={setSettings} />
        <div className="flex gap-2 mb-8 border-b border-ink/15" data-testid="social-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`overline px-4 py-3 -mb-px border-b-2 transition-colors ${tab === k ? "border-ink text-ink" : "border-transparent text-ink/45 hover:text-ink"}`} data-testid={`social-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "campaign" && <CampaignComposer onCreated={() => { load(); setTab("instagram"); }} />}
        {tab === "calendar" && <Planner posts={igPosts} settings={settings} onChanged={load} />}
        {tab === "outreach" && <Outreach />}
        {tab === "tiktok" && (
          <>
            <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="tiktok-panel">
              <div className="relative z-10 space-y-2">
                <p className="overline text-gloss-gold">TikTok Desk — 2 minutes per post</p>
                <p className="font-sans text-sm text-cream/60 max-w-2xl">TikTok doesn't allow outside tools to post yet, so this desk preps everything for a fast manual upload: hit <b>Make tiles</b> and download the <b>9:16</b>, or <b>Animate</b> for a motion clip — then <b>Copy caption</b> and <b>Copy reel script</b>, and paste it all into the TikTok app.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tiktok-grid">
              {ttPosts.map((p) => <PostCard key={p.id} p={p} settings={settings} onDelete={remove} onChanged={load} mode="tiktok" />)}
              {ttPosts.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50 lg:col-span-2">No TikTok posts yet — design one in the Campaign Composer and pick TikTok.</p>}
            </div>
          </>
        )}
        {tab === "instagram" && (
          <>
            <DemoReelPanel />
            <ClipsPanel />
            <ShowcasePanel onMade={load} />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <p className="font-sans text-sm text-ink/50">Drafts post themselves once queued — one each morning keeps the feed alive.</p>
              <button onClick={autoSchedule} disabled={busy} className="overline px-4 py-2.5 border border-ink/30 hover:bg-ink hover:text-cream transition-colors flex items-center gap-2 disabled:opacity-60" data-testid="auto-schedule-btn">
                <CalendarClock size={14} /> Queue all drafts — 9am daily
              </button>
            </div>
            <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="generate-social-panel">
              <div className="relative z-10">
                <p className="overline text-gloss-gold mb-4">Create a post</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <select value={type} onChange={(e) => setType(e.target.value)} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="social-type">
                    {["article teaser", "cover reveal", "member spotlight", "quote card", "reel script"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input value={context} onChange={(e) => setContext(e.target.value)} placeholder="Subject e.g. Amara Okafor, Maison Verre"
                    className="flex-1 bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold transition-colors" data-testid="social-context" />
                  <button onClick={generate} disabled={busy} className="bg-cream text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="generate-social-btn">
                    {busy ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />} Create
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="social-grid">
              {igPosts.map((p) => <PostCard key={p.id} p={p} settings={settings} onDelete={remove} onChanged={load} mode="instagram" />)}
              {igPosts.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50 lg:col-span-2">No Instagram posts yet — create one above, design a campaign, or generate from a member's Hub.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
